import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { authApi } from '@/api/auth'
import { useState } from 'react'
import {
  TextField,
  Button,
  Alert,
  Box,
  InputAdornment,
  Typography,
} from '@mui/material'
import { Lock, CheckCircle } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'

interface ResetPasswordFormProps {
  token: string
  email: string
}

const createResetPasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      password: z
        .string()
        .min(1, t('auth.validation.passwordRequired'))
        .min(8, t('auth.validation.passwordMin')),
      password_confirmation: z.string().min(1, t('auth.validation.passwordRequired')),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t('auth.validation.passwordMismatch'),
      path: ['password_confirmation'],
    })

export function ResetPasswordForm({ token, email }: ResetPasswordFormProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const resetPasswordSchema = createResetPasswordSchema(t)
  type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', password_confirmation: '' },
  })

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError(null)
      setIsLoading(true)
      await authApi.resetPassword({
        token,
        email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      })
      setIsSuccess(true)
    } catch (err: unknown) {
      const requestError = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage =
        requestError.response?.data?.message || requestError.message || t('auth.resetPasswordError')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
        </Box>
        <Typography variant="h5" component="h2" gutterBottom fontWeight={700}>
          {t('auth.resetPasswordSuccessTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.resetPasswordSuccessMessage')}
        </Typography>
        <Button component={Link} to={ROUTES.LOGIN} variant="contained" size="large">
          {t('auth.backToLogin')}
        </Button>
      </Box>
    )
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.newPassword')}
            type="password"
            fullWidth
            autoComplete="new-password"
            error={!!errors.password}
            helperText={errors.password?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        name="password_confirmation"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.passwordConfirmation')}
            type="password"
            fullWidth
            autoComplete="new-password"
            error={!!errors.password_confirmation}
            helperText={errors.password_confirmation?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{ mt: 1, py: 1.5 }}
      >
        {isLoading ? t('common.loading') : t('auth.resetPasswordSubmit')}
      </Button>
    </Box>
  )
}
